/**
 * ComposerDialog — pełen email composer dla MASKIL Inbox (Tier 2, 2026-05-13).
 *
 * Flow:
 *   1. Open (mode: 'new' | 'reply' | 'forward')
 *   2. (Optional) Pick template — auto-renders subject + body
 *   3. Edit fields (To/CC/Subject/Body)
 *   4. Validate required context (e.g. sender_name for templates)
 *   5. Send via /emails/compose → SMTP via ada@maskil.pl, Reply-To zapytania@
 *   6. On success: toast + close + refresh inbox list
 *
 * Template variables modal — appears if template needs more context
 * (sender_name / decision_deadline / questions etc.) — user fills + re-renders.
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Send, FileText, Loader2, X, Sparkles, AlertCircle, Mail, Reply, Forward,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import PlanLock from '../common/PlanLock';


const MODE_LABELS = {
  new: { pl: 'Nowy mail', en: 'New email', icon: Mail },
  reply: { pl: 'Odpowiedź', en: 'Reply', icon: Reply },
  forward: { pl: 'Przekazanie', en: 'Forward', icon: Forward },
};


// Friendly labels for template context fields (used in side panel)
const FIELD_LABELS_PL = {
  sender_name: 'Twoje imię i nazwisko',
  sender_company: 'Firma',
  sender_email: 'Email zwrotny (zwykle ada@maskil.pl)',
  sender_phone: 'Telefon kontaktowy',
  decision_deadline: 'Termin decyzji (data lub "+5 dni robocze")',
  response_deadline: 'Termin oczekiwanej odpowiedzi',
  rfq_reference: 'Numer zapytania / RFQ',
  questions: 'Pytania (jedno na linię)',
  proposed_changes: 'Proponowane zmiany',
  rejection_reason_short: 'Powód odmowy (opcjonalnie, np. "ze względu na cenę")',
  next_opportunity_window: 'Kolejna okazja (opcjonalnie)',
  recipient_name: 'Imię odbiorcy (wewnętrzny adresat)',
  my_note: 'Komentarz / zalecana akcja',
  topic_short: 'Temat (krótko)',
  linked_tender: 'Powiązany przetarg',
  action_deadline: 'Termin reakcji',
  original_body: 'Oryginalna treść (zostanie cytowana)',
  quote_date: 'Data oferty',
};


function TemplateContextEditor({ template, context, onChange, lang }) {
  if (!template) return null;
  const allFields = [
    ...(template.required_fields || []),
    ...(template.optional_fields || []),
  ];

  return (
    <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
      {allFields.map((field) => {
        const isRequired = template.required_fields?.includes(field);
        const label = FIELD_LABELS_PL[field] || field;
        const isMultiline = field === 'questions' || field === 'proposed_changes' || field === 'my_note' || field === 'original_body';
        return (
          <div key={field}>
            <Label className="text-xs flex items-center gap-1">
              {label}
              {isRequired && <span className="text-red-400">*</span>}
            </Label>
            {isMultiline ? (
              <Textarea
                value={context[field] || ''}
                onChange={(e) => onChange({ ...context, [field]: e.target.value })}
                rows={3}
                className="text-sm mt-0.5"
                placeholder={field === 'questions' ? '1. ...\n2. ...' : ''}
              />
            ) : (
              <Input
                value={context[field] || ''}
                onChange={(e) => onChange({ ...context, [field]: e.target.value })}
                className="text-sm mt-0.5 h-8"
              />
            )}
          </div>
        );
      })}
    </div>
  );
}


export default function ComposerDialog({
  open,
  onClose,
  mode = 'new',          // 'new' | 'reply' | 'forward'
  initialEmail = null,   // for reply/forward — the original email object
  defaultTo = '',
  defaultSubject = '',
  defaultBody = '',
  linkedTenderId = null,
  linkedSupplierId = null,
  lang = 'pl',
  onSent,
}) {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateContext, setTemplateContext] = useState({});

  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  const [sending, setSending] = useState(false);
  const [renderingTemplate, setRenderingTemplate] = useState(false);
  const [renderWarnings, setRenderWarnings] = useState([]);
  const [missingRequired, setMissingRequired] = useState([]);

  // Reset state when dialog opens with new context
  useEffect(() => {
    if (!open) return;
    setTo(defaultTo || (initialEmail?.from_addr || ''));
    setCc('');
    if (mode === 'reply') {
      setSubject(defaultSubject || `Re: ${initialEmail?.subject || ''}`);
      setBody(defaultBody || '');
    } else if (mode === 'forward') {
      setSubject(defaultSubject || `Fwd: ${initialEmail?.subject || ''}`);
      setBody(defaultBody || `\n\n---\nOryginalna wiadomość:\nOd: ${initialEmail?.from_addr || ''}\nData: ${(initialEmail?.date || '').slice(0, 16)}\nTemat: ${initialEmail?.subject || ''}\n\n${initialEmail?.body_text || ''}`);
    } else {
      setSubject(defaultSubject);
      setBody(defaultBody);
    }
    setSelectedTemplate(null);
    setTemplateContext({});
    setRenderWarnings([]);
    setMissingRequired([]);
  }, [open, mode, initialEmail, defaultTo, defaultSubject, defaultBody]);

  // Load templates on open
  useEffect(() => {
    if (!open) return;
    api.get('/emails/templates/list', { params: { lang } })
      .then((r) => setTemplates(r.data.items || []))
      .catch(() => setTemplates([]));
  }, [open, lang]);

  async function applyTemplate(tplId) {
    const tpl = templates.find((t) => t.id === tplId);
    setSelectedTemplate(tpl || null);
    if (!tpl) return;
    setRenderingTemplate(true);
    try {
      const r = await api.post('/emails/templates/render', {
        template_id: tplId,
        context: templateContext,
        lang,
        reply_to_email_id: mode === 'reply' ? (initialEmail?.id || null) : null,
      });
      setSubject(r.data.subject);
      setBody(r.data.body);
      setRenderWarnings(r.data.warnings || []);
      setMissingRequired(r.data.missing_required || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Render error');
    } finally {
      setRenderingTemplate(false);
    }
  }

  async function reRenderTemplate(newContext) {
    setTemplateContext(newContext);
    if (!selectedTemplate) return;
    try {
      const r = await api.post('/emails/templates/render', {
        template_id: selectedTemplate.id,
        context: newContext,
        lang,
        reply_to_email_id: mode === 'reply' ? (initialEmail?.id || null) : null,
      });
      setSubject(r.data.subject);
      setBody(r.data.body);
      setRenderWarnings(r.data.warnings || []);
      setMissingRequired(r.data.missing_required || []);
    } catch (err) {
      // silent — keep last render
    }
  }

  async function handleSend() {
    if (!to || !to.includes('@')) {
      toast.error(lang === 'pl' ? 'Wpisz poprawny adres email' : 'Enter valid email');
      return;
    }
    if (!subject.trim()) {
      toast.error(lang === 'pl' ? 'Brak tematu' : 'Subject required');
      return;
    }
    if (!body.trim()) {
      toast.error(lang === 'pl' ? 'Brak treści' : 'Body required');
      return;
    }
    if (body.includes('{') && body.includes('}')) {
      const ok = window.confirm(lang === 'pl'
        ? 'Treść zawiera nieuzupełnione pola (np. {sender_name}). Czy na pewno wysłać?'
        : 'Body contains unfilled placeholders (e.g. {sender_name}). Send anyway?');
      if (!ok) return;
    }

    setSending(true);
    try {
      const r = await api.post('/emails/compose', {
        to,
        cc: cc || null,
        subject,
        body,
        reply_to_email_id: mode === 'reply' ? (initialEmail?.id || null) : null,
        linked_tender_id: linkedTenderId,
        linked_supplier_id: linkedSupplierId,
        template_id_used: selectedTemplate?.id || null,
      });
      if (r.data.status === 'sent') {
        toast.success(lang === 'pl' ? '✉ Wysłano email' : 'Email sent');
      } else if (r.data.status === 'mock_sent') {
        toast.info(lang === 'pl'
          ? 'Email zapisany (tryb mock — SMTP nie skonfigurowany)'
          : 'Email logged (mock mode — SMTP not configured)');
      } else {
        toast.error(r.data.provider_response?.error || (lang === 'pl' ? 'Błąd wysyłki' : 'Send failed'));
      }
      if (onSent) onSent(r.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || (lang === 'pl' ? 'Błąd' : 'Error'));
    } finally {
      setSending(false);
    }
  }

  const modeInfo = MODE_LABELS[mode] || MODE_LABELS.new;
  const ModeIcon = modeInfo.icon;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="composer-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ModeIcon className="w-4 h-4 text-primary" />
            {lang === 'pl' ? modeInfo.pl : modeInfo.en}
            {selectedTemplate && (
              <Badge variant="outline" className="text-[10px] ml-1">
                {selectedTemplate.label}
              </Badge>
            )}
          </DialogTitle>
          {linkedTenderId && (
            <DialogDescription className="text-xs">
              {lang === 'pl' ? 'Powiązany przetarg:' : 'Linked tender:'}{' '}
              <code className="text-primary">{linkedTenderId}</code>
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="grid grid-cols-3 gap-4">
          {/* Left — main form */}
          <div className="col-span-2 space-y-2">
            <div>
              <Label className="text-xs">To *</Label>
              <Input
                value={to} onChange={(e) => setTo(e.target.value)}
                placeholder="adres@firma.pl"
                className="h-8"
                data-testid="composer-to"
              />
            </div>
            <div>
              <Label className="text-xs">CC</Label>
              <Input
                value={cc} onChange={(e) => setCc(e.target.value)}
                placeholder="(opcjonalnie)"
                className="h-8"
              />
            </div>
            <div>
              <Label className="text-xs">{lang === 'pl' ? 'Temat *' : 'Subject *'}</Label>
              <Input
                value={subject} onChange={(e) => setSubject(e.target.value)}
                className="h-8"
                data-testid="composer-subject"
              />
            </div>
            <div>
              <Label className="text-xs">{lang === 'pl' ? 'Treść *' : 'Body *'}</Label>
              <Textarea
                value={body} onChange={(e) => setBody(e.target.value)}
                rows={14}
                className="font-mono text-[13px]"
                data-testid="composer-body"
              />
              {body.includes('{') && body.includes('}') && (
                <p className="text-[11px] text-amber-400 mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {lang === 'pl'
                    ? 'Treść zawiera nieuzupełnione pola (np. {sender_name}). Wpisz je w panelu po prawej lub bezpośrednio w treści.'
                    : 'Body contains unfilled placeholders. Fill them in the side panel or directly in body.'}
                </p>
              )}
            </div>
          </div>

          {/* Right — template picker + context (PRO feature) */}
          <div className="col-span-1 space-y-3">
            <div>
              <Label className="text-xs flex items-center gap-1">
                <Sparkles className="w-3 h-3 text-primary" />
                {lang === 'pl' ? 'Szablon' : 'Template'}
                <PlanLock feature="inbox_composer_templates" requiredPlan="pro" inline lang={lang}>
                  <span />
                </PlanLock>
              </Label>
              <Select
                value={selectedTemplate?.id || ''}
                onValueChange={applyTemplate}
                disabled={templates.length === 0}
              >
                <SelectTrigger className="h-8 text-xs mt-0.5" data-testid="composer-template-picker">
                  <SelectValue placeholder={lang === 'pl' ? '— brak szablonu —' : '— no template —'} />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      <div className="flex flex-col items-start">
                        <span>{t.label}</span>
                        <span className="text-[10px] text-muted-foreground">{t.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {renderingTemplate && (
                <p className="text-[10px] text-muted-foreground mt-1 flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  {lang === 'pl' ? 'Renderuję…' : 'Rendering…'}
                </p>
              )}
            </div>

            {selectedTemplate && (
              <div className="border border-border rounded-md p-2 bg-muted/20">
                <div className="text-[10px] font-semibold uppercase text-muted-foreground mb-1.5">
                  {lang === 'pl' ? 'Pola szablonu' : 'Template fields'}
                </div>
                <TemplateContextEditor
                  template={selectedTemplate}
                  context={templateContext}
                  onChange={reRenderTemplate}
                  lang={lang}
                />
                {missingRequired.length > 0 && (
                  <p className="text-[10px] text-red-400 mt-2 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {lang === 'pl' ? 'Brakuje wymaganych pól: ' : 'Missing required: '}
                    {missingRequired.join(', ')}
                  </p>
                )}
              </div>
            )}

            {renderWarnings.length > 0 && (
              <div className="text-[10px] text-amber-400 space-y-0.5">
                {renderWarnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
              </div>
            )}

            <div className="text-[10px] text-muted-foreground border-t border-border pt-2 leading-relaxed">
              <strong>FROM:</strong> ada@maskil.pl<br/>
              <strong>Reply-To:</strong> zapytania@maskil.pl<br/>
              {lang === 'pl'
                ? 'Odpowiedzi wracają do zapytania@maskil.pl — pojawią się w Inbox po następnym sync.'
                : 'Replies route to zapytania@maskil.pl — visible in Inbox after next sync.'}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={sending}>
            {lang === 'pl' ? 'Anuluj' : 'Cancel'}
          </Button>
          <Button
            onClick={handleSend}
            disabled={sending || !to || !subject}
            data-testid="composer-send"
            className="gap-1"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {lang === 'pl' ? 'Wyślij' : 'Send'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
